import { Link } from "@/components/Link";
import type { Category } from "@/api/categoryService";

// Deterministic oklch color from a seed string (stable across renders)
function tone(seed: string, l = 0.55, c = 0.16): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) % 360;
  }
  return `oklch(${l} ${c} ${h})`;
}

// Format review count: >=1000 → "8.2k" (1 decimal, no trailing .0); <1000 → as-is
function formatCount(n: number): string {
  if (n >= 1000) {
    return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  return String(n);
}

type AvatarTileProps = {
  store: { id: string; name: string; logoUrl?: string } | null;
  index: number;
};

function AvatarTile({ store, index }: AvatarTileProps) {
  const baseStyle: React.CSSProperties = {
    aspectRatio: "1",
    borderRadius: "8px",
    overflow: "hidden",
  };

  if (!store) {
    return (
      <div
        style={{
          ...baseStyle,
          backgroundColor: "#f4efe8",
        }}
      />
    );
  }

  if (store.logoUrl) {
    return (
      <div style={baseStyle}>
        <img
          src={store.logoUrl}
          alt={store.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>
    );
  }

  // Fallback: deterministic color + white initial
  const initial = store.name.charAt(0);
  const seed = store.id + initial + String(index);
  return (
    <div
      style={{
        ...baseStyle,
        backgroundColor: tone(seed),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: "12px",
        fontWeight: 700,
      }}
    >
      {initial}
    </div>
  );
}

type CategoryCardProps = {
  category: Category;
};

export function CategoryCard({ category }: CategoryCardProps) {
  const { slug, title, description, storeCount, reviewCount, topStores } = category;

  // Build 5-cell store array (pad with null if fewer than 5)
  const storeCells: (typeof topStores[0] | null)[] = [...topStores];
  while (storeCells.length < 5) storeCells.push(null);

  const overflowCount = Math.max(0, storeCount - 5);

  return (
    <Link to={`/category/${slug}`}>
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #ece7e1",
          borderRadius: "16px",
          padding: "24px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: "22px",
          height: "100%",
          boxSizing: "border-box",
          transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        }}
        className="category-card-hover"
      >
        {/* Head row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px" }}>
          {/* Title block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: "19px",
                fontWeight: 700,
                lineHeight: 1.3,
                letterSpacing: "-0.005em",
                color: "#14110f",
              }}
            >
              {title.ar}
            </div>

            {description && (
              <div
                style={{
                  fontSize: "13px",
                  color: "#3a3530",
                  marginTop: "8px",
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {description}
              </div>
            )}

            {reviewCount > 0 && (
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "11px",
                  color: "#736b63",
                  marginTop: "10px",
                }}
              >
                <b style={{ color: "#14110f", fontWeight: 600 }}>{formatCount(reviewCount)}</b>
                {" "}تقييم
              </div>
            )}
          </div>

          {/* Store count block */}
          {storeCount > 0 && (
            <div style={{ flexShrink: 0, textAlign: "end" }}>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "24px",
                  fontWeight: 600,
                  color: "#14110f",
                  letterSpacing: "-0.015em",
                  lineHeight: 1,
                }}
              >
                {storeCount}
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "10.5px",
                  color: "#736b63",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  marginTop: "4px",
                }}
              >
                متجر
              </div>
            </div>
          )}
        </div>

        {/* Avatar strip */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "6px",
            marginTop: "auto",
          }}
        >
          {storeCells.map((store, i) => (
            <AvatarTile key={store?.id ?? `empty-${i}`} store={store} index={i} />
          ))}

          {/* Overflow cell (6th) */}
          <div
            style={{
              aspectRatio: "1",
              borderRadius: "8px",
              backgroundColor: "#f4efe8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#736b63",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11.5px",
            }}
          >
            {overflowCount > 0 ? `+${overflowCount}` : null}
          </div>
        </div>
      </div>
    </Link>
  );
}

export function CategoryCardSkeleton() {
  const tileStyle: React.CSSProperties = {
    aspectRatio: "1",
    borderRadius: "8px",
    backgroundColor: "#f4efe8",
  };

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #ece7e1",
        borderRadius: "16px",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "22px",
        height: "100%",
        boxSizing: "border-box",
      }}
      className="animate-pulse"
    >
      {/* Head row skeleton */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ height: "19px", width: "75%", borderRadius: "4px", backgroundColor: "#f4efe8" }} />
          <div style={{ height: "13px", width: "100%", borderRadius: "4px", backgroundColor: "#f4efe8", marginTop: "8px" }} />
          <div style={{ height: "11px", width: "33%", borderRadius: "4px", backgroundColor: "#f4efe8", marginTop: "10px" }} />
        </div>
        <div style={{ flexShrink: 0 }}>
          <div style={{ height: "24px", width: "48px", borderRadius: "4px", backgroundColor: "#f4efe8" }} />
        </div>
      </div>

      {/* Strip skeleton */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "6px", marginTop: "auto" }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={tileStyle} />
        ))}
      </div>
    </div>
  );
}
